import { useState } from "react";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import "./ProductPage.scss";
import interviewPackQuestion from "../../assets/products/interview-pack-question.png";
import interviewPackQuestionWithMarkScheme from "../../assets/products/interview-pack-question-with-mark-scheme.png";
import { FaCheckCircle, FaBullseye, FaBrain, FaStethoscope, FaBalanceScale, FaBriefcase, FaUsers, FaClipboardList, FaChartLine, FaFlag } from "react-icons/fa";

interface InterviewPackPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const InterviewPackPage = ({ product }: InterviewPackPageProps) => {
  const [showMarkScheme, setShowMarkScheme] = useState(false);

  const interviewDomains = [
    {
      title: "Clinical Scenarios",
      icon: <FaStethoscope />,
      items: [
        "Acute ophthalmic emergencies",
        "Common on-call presentations",
        "Safe initial management and escalation",
        "Prioritisation and decision-making"
      ]
    },
    {
      title: "Ethical & Professional Scenarios",
      icon: <FaBalanceScale />,
      items: [
        "Consent and capacity",
        "Complaints and difficult conversations",
        "Duty of candour",
        "Patient safety and governance"
      ]
    },
    {
      title: "Portfolio & Commitment to Specialty",
      icon: <FaBriefcase />,
      items: [
        "Motivation for ophthalmology",
        "Career insight and reflection",
        "Audit, research, teaching, and leadership",
        "Demonstrating long-term commitment"
      ]
    },
    {
      title: "Communication & Teamwork",
      icon: <FaUsers />,
      items: [
        "Managing conflict",
        "MDT working",
        "Leadership and teamwork",
        "Patient-centred communication"
      ]
    }
  ];

  const features = [
    {
      icon: <FaClipboardList />,
      title: "Framework-Based Preparation",
      description: "Clear answer frameworks aligned with interview scoring domains, key themes interviewers expect, and prompts to structure responses logically.",
      items: [
        "Answer frameworks aligned with scoring domains",
        "Key themes interviewers expect",
        "Prompts to structure responses",
        "Common pitfalls to avoid"
      ]
    },
    {
      icon: <FaChartLine />,
      title: "Improve Structure & Confidence",
      description: "Practising with structured scenarios helps you organise thoughts quickly and communicate clearly under pressure.",
      items: [
        "Organise thoughts quickly",
        "Communicate clearly under pressure",
        "Demonstrate insight and reflection",
        "Avoid rambling or unsafe responses"
      ]
    },
    {
      icon: <FaFlag />,
      title: "Built for UK ST Recruitment",
      description: "Everything is designed around what interview panels assess, with NHS-relevant scenarios and safety-focused clinical thinking.",
      items: [
        "NHS-relevant scenarios",
        "Aligned with scoring domains",
        "Safety-focused clinical thinking",
        "Emphasis on professionalism"
      ]
    }
  ];

  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="Ophthalmology ST1 interview pack">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <p className="oq-product-hero__eyebrow">ST1 Ophthalmology interviews</p>
              <h1 className="oq-product-hero__title">Ophthalmology ST Interview Scenarios</h1>
              <p className="oq-product-hero__lead">
                75+ high-yield interview scenarios covering all major interview domains. Framework-based preparation
                that scores marks.
              </p>
              <div className="oq-product-hero__pricing">
                <span className="oq-product-hero__price">£{product.price}</span>
                <span className="oq-product-hero__length">— {product.length}</span>
              </div>
              {!product.comingSoon && (
                <div className="oq-product-hero__signup">
                  <Button
                    text="Buy Interview Pack Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              )}
            </div>
            <div className="oq-product-hero__visual">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={interviewPackQuestionWithMarkScheme}
                  alt="Ophthalmology ST interview scenario with mark scheme and scoring guidance"
                  width={3156}
                  height={1778}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Scenarios Section */}
            <section className="sales-section interview-scenarios-section">
              <div className="section-header">
                <FaBullseye className="section-icon" />
                <h2>75+ High-Yield Ophthalmology Interview Scenarios</h2>
              </div>
              <p className="lead-text">
                Access 75+ carefully curated interview scenarios, covering all major interview domains:
              </p>
              <div className="interview-domains-list">
                {interviewDomains.map((domain, index) => (
                  <div key={index} className="interview-domain">
                    <div className="interview-domain-header">
                      <div className="interview-domain-icon">{domain.icon}</div>
                      <h3>{domain.title}</h3>
                    </div>
                    <ul>
                      {domain.items.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Full-Width Image Section */}
            <section className="sales-section interview-image-section">
              <div className="interview-image-container">
                <div className="interview-image-wrapper">
                  <img 
                    key={showMarkScheme ? "mark-scheme" : "question"}
                    src={showMarkScheme ? interviewPackQuestionWithMarkScheme : interviewPackQuestion} 
                    alt={showMarkScheme ? "Interview Question with Mark Scheme" : "Interview Question"} 
                    className={`interview-image ${showMarkScheme ? "show" : ""}`}
                  />
                </div>
                <div className="interview-image-cta">
                  <button 
                    className="mark-scheme-toggle"
                    onClick={() => setShowMarkScheme(!showMarkScheme)}
                  >
                    {showMarkScheme ? "Hide Mark Scheme" : "Show Mark Scheme"}
                  </button>
                </div>
              </div>
            </section>

            {/* Features Section - 3 Columns */}
            <section className="sales-section interview-features-section">
              <div className="interview-features-grid">
                {features.map((feature, index) => (
                  <div key={index} className="interview-feature-card">
                    <div className="interview-feature-icon">{feature.icon}</div>
                    <h3>{feature.title}</h3>
                    <p className="interview-feature-description">{feature.description}</p>
                    <ul className="interview-feature-list">
                      {feature.items.map((item, itemIndex) => (
                        <li key={itemIndex}>
                          <FaCheckCircle className="feature-check-icon" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Pricing Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Simple Pricing</h3>
                <h3 className="pricing-card__subtitle">Ophthalmology ST Interview Scenarios</h3>
                <div className="pricing-amount">
                  <span className="price-large">£{product.price}</span>
                  <span className="price-period">— full access</span>
                </div>
                <div className="pricing-includes">
                  <h4>Includes:</h4>
                  <ul>
                    <li><Checkmark /> 75+ interview scenarios</li>
                    <li><Checkmark /> Examiner-focused frameworks</li>
                    <li><Checkmark /> Structured prompts and guidance</li>
                  </ul>
                </div>
                <div className="pricing-cta">
                  <Button
                    text="Get Started Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default InterviewPackPage;

